import { getRegistryBase, getRegistryBaseForWorkspace } from './client'
import type {
  RegistryFunctionConfig,
  RegistryFunctionInstanceStats,
  RegistryFunctionInstanceStatus,
  RegistryFunctionStatus,
  RegistryFunctionStats,
  RegistryMutationResponse,
  RegistryTriggerResponse
} from './types'
import type { UpdateOptions } from '@streamnative/pulsar-admin-client-typescript'
import type { FunctionState } from '@streamnative/pulsar-admin-client-typescript'

// === Workspace-scoped Function APIs ===

export const listFunctionsForWorkspace = async (org?: string, workspaceUid?: string) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<string[]>(`${basePath}/functions`)
}

export const getFunctionInfoForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<RegistryFunctionConfig>(`${basePath}/functions/${functionName}`)
}

export const getFunctionStatusForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<RegistryFunctionStatus>(`${basePath}/functions/${functionName}/status`)
}

export const getFunctionStatsForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<RegistryFunctionStats>(`${basePath}/functions/${functionName}/stats`)
}

export const startFunctionForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/functions/${functionName}:start`)
}

export const stopFunctionForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/functions/${functionName}:stop`)
}

export const restartFunctionForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/functions/${functionName}:restart`)
}

export const triggerFunctionForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryTriggerResponse>(`${basePath}/functions/${functionName}:trigger`)
}

export const deleteFunctionForWorkspace = async (
  functionName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.delete<RegistryMutationResponse>(`${basePath}/functions/${functionName}`)
}

// === Function APIs (WithDefaults) ===

export const listFunctionsWithDefaults = async () => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<string[]>(`${basePath}/functions`)
}

export const getFunctionInfoWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistryFunctionConfig>(`${basePath}/functions/${functionName}`)
}

export const registerFunctionWithDefaults = async (
  functionName: string,
  functionConfig: RegistryFunctionConfig,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/functions/${functionName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append('functionConfig', JSON.stringify(functionConfig))

  return axios.post<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const updateFunctionWithDefaults = async (
  functionName: string,
  functionConfig: RegistryFunctionConfig,
  updateOptions?: UpdateOptions,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/functions/${functionName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append('functionConfig', JSON.stringify(functionConfig))
  if (updateOptions) {
    formData.append('updateOptions', JSON.stringify(updateOptions))
  }

  return axios.put<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const deregisterFunctionWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.delete<RegistryMutationResponse>(`${basePath}/functions/${functionName}`)
}

export const getFunctionStatusWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistryFunctionStatus>(`${basePath}/functions/${functionName}/status`)
}

export const getFunctionStatsWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistryFunctionStats>(`${basePath}/functions/${functionName}/stats`)
}

export const getFunctionStateWithDefaults = async (functionName: string, key: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<FunctionState>(`${basePath}/functions/${functionName}/state/${key}`)
}

export const putFunctionStateWithDefaults = async (
  functionName: string,
  key: string,
  state: any
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/functions/${functionName}/state/${key}`
  const formData = new FormData()
  formData.append('state', state)

  return axios.post<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const getFunctionInstanceStatusWithDefaults = async (
  functionName: string,
  instanceId: number
) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistryFunctionInstanceStatus>(
    `${basePath}/functions/${functionName}/${instanceId}/status`
  )
}

export const getFunctionInstanceStatsWithDefaults = async (
  functionName: string,
  instanceId: number
) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistryFunctionInstanceStats>(
    `${basePath}/functions/${functionName}/${instanceId}/stats`
  )
}

export const restartFunctionWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/functions/${functionName}:restart`)
}

export const startFunctionWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/functions/${functionName}:start`)
}

export const stopFunctionWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/functions/${functionName}:stop`)
}

export const triggerFunctionWithDefaults = async (
  functionName: string,
  data?: string,
  dataStream?: any,
  topic?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/functions/${functionName}:trigger`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (dataStream) {
    formData.append('dataStream', dataStream)
  }
  if (topic) {
    formData.append('topic', topic)
  }

  return axios.post<RegistryTriggerResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const restartFunctionInstanceWithDefaults = async (
  functionName: string,
  instanceId: number
) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/functions/${functionName}/${instanceId}:restart`
  )
}

export const startFunctionInstanceWithDefaults = async (
  functionName: string,
  instanceId: number
) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/functions/${functionName}/${instanceId}:start`
  )
}

export const stopFunctionInstanceWithDefaults = async (
  functionName: string,
  instanceId: number
) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/functions/${functionName}/${instanceId}:stop`
  )
}
